import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { FileText, ExternalLink, Download, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import { BrandWatermark } from '@/components/BrandWatermark';
import TradeJournal from '@/components/TradeJournal';
import { UserBadgesLoader } from '@/components/UserBadges';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getResourceIcon = (type) => {
  switch (type) {
    case 'pdf':
      return <FileText className="h-5 w-5 text-red-400" />;
    case 'document':
      return <FileText className="h-5 w-5 text-blue-400" />;
    default:
      return <ExternalLink className="h-5 w-5 text-emerald-400" />;
  }
};

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      toast.error('Error al cargar recursos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020402]">
      <Navbar />
      <BrandWatermark />
      
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <h1 
              className="text-4xl md:text-5xl font-bold mb-4 text-emerald-50"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
              data-testid="resources-title"
            >
              <span className="flex items-center space-x-3">
                <span>MATERIAL DE APOYO</span>
                <UserBadgesLoader size="md" />
              </span>
            </h1>
            <p className="text-emerald-100/70">Guías, documentos y recursos para mejorar tu trading</p>
          </div>

          {/* Calendly CTA */}
          <a
            href="https://calendly.com/ricardopazfx/30min"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calendly-btn"
          >
            <div className="glass-panel rounded-lg p-6 mb-8 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer alien-glow group">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                    <CalendarCheck className="h-6 w-6 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                      AGENDA TU SEGUIMIENTO
                    </h3>
                    <p className="text-emerald-100/60 text-sm">Seguimiento personalizado de 30 minutos con nuestro equipo</p>
                  </div>
                </div>
                <Button className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold uppercase tracking-wider">
                  Agendar
                </Button>
              </div>
            </div>
          </a>

          {/* Bitácora Operativa */}
          <div className="mb-8">
            <TradeJournal />
          </div>

          {/* Resources Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent"></div>
              <p className="text-emerald-100/70 mt-4">Cargando recursos...</p>
            </div>
          ) : resources.length === 0 ? (
            <div className="glass-panel rounded-lg p-12 text-center" data-testid="no-resources">
              <FileText className="h-16 w-16 text-emerald-500/50 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2 text-emerald-100">No hay recursos disponibles</h3>
              <p className="text-emerald-100/60">Los nuevos recursos aparecerán aquí cuando sean publicados.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resources-grid">
              {resources.map((resource) => (
                <div 
                  key={resource.id}
                  className="glass-panel rounded-lg p-6 hover:border-emerald-500/50 transition-all duration-300"
                  data-testid={`resource-${resource.id}`}
                >
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="w-10 h-10 bg-black/40 rounded flex items-center justify-center">
                      {getResourceIcon(resource.resource_type)}
                    </div>
                    <div className="flex-1">
                      <h3 
                        className="text-lg font-bold text-emerald-50"
                        style={{ fontFamily: 'Rajdhani, sans-serif' }}
                      >
                        {resource.title}
                      </h3>
                      <span className="text-xs text-emerald-400/70 uppercase">
                        {resource.resource_type}
                      </span>
                    </div>
                  </div>

                  {resource.description && (
                    <p className="text-emerald-100/70 text-sm mb-4">{resource.description}</p>
                  )}

                  <a 
                    href={resource.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    data-testid={`resource-link-${resource.id}`}
                  >
                    <Button 
                      className="w-full bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Descargar / Ver
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}